import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { CATERING_FORM_FIELDS } from 'pages/cart/details/types';
import { useOrderContext } from 'state/order';
import { useCanadaOrderScope } from 'state/order/hooks/use-canada-order-policy';
import { usePaymentContext } from 'state/payment';

import { IGetErrorMessagesProps } from './types';

export const useGetPaymentErrors = () => {
  const { cartPriceLimitExceeded, cartPriceTooLow, cartCateringPriceTooLow } = useOrderContext();
  const omitDeliveryLimitErrors = useCanadaOrderScope() === 'canada-delivery';
  const { checkoutPaymentMethodId } = usePaymentContext();
  const [pricedAndAvailable, setPricedAndAvailable] = useState(false);
  const formatters = useIntl();

  useEffectOnUpdates(() => {
    //If payment method id changes, priced and available should be true until a new pricing is fired
    setPricedAndAvailable(true);
  }, [checkoutPaymentMethodId]);

  const getPaymentErrorMessageWithFields = useCallback(
    (errorFields: Array<CATERING_FORM_FIELDS | string> = ['details']) => {
      const formattedFields = errorFields
        ?.map((field: string) => formatters.formatMessage({ id: field }).toLowerCase())
        ?.join(', ');

      return formatters.formatMessage(
        { id: 'paymentDetailsErrorWithFields' },
        { fields: formattedFields }
      );
    },
    [formatters]
  );

  const getErrorMessages = useCallback(
    ({
      areDeliveryDetailsWithError,
      invalidAddressLine2,
      cartCateringPriceTooLow: serverOrderCartCateringPriceTooLow,
      cartPriceTooLow: serverOrderCartPriceTooLow,
      checkoutPaymentMethodNeedRevaultError,
      curbsideOrderError,
      invalidOfferError,
      invalidPaymentMethodError,
      invalidReloadError,
      invalidTipAmountError,
      noDirectPayAndNoPrepaidMethodError,
      pricedAndAvailableError,
      storeIsClosing,
      unableToReloadCardError,
      cartDetailsErrorFields,
    }: IGetErrorMessagesProps) => {
      const paymentErrors = [
        { messageId: 'curbsideOrderError', hasError: curbsideOrderError },
        { messageId: 'invalidOfferError', hasError: invalidOfferError },
        { messageId: 'invalidPaymentMethodError', hasError: invalidPaymentMethodError },
        { messageId: 'invalidTipAmountError', hasError: invalidTipAmountError },
        {
          messageId: 'maximumPriceError',
          hasError: !omitDeliveryLimitErrors && !!cartPriceLimitExceeded,
        },
        {
          messageId: 'minimumPriceError',
          hasError: !omitDeliveryLimitErrors && (!!cartPriceTooLow || !!serverOrderCartPriceTooLow),
        },
        {
          messageId: 'minimumPriceForCateringError',
          hasError: !!cartCateringPriceTooLow || !!serverOrderCartCateringPriceTooLow,
        },
        { messageId: 'unableToReloadCardError', hasError: unableToReloadCardError },
        {
          messageId: 'pricedAndAvailableError',
          hasError: pricedAndAvailableError && !pricedAndAvailable,
        },
        {
          messageId: '',
          message: getPaymentErrorMessageWithFields(cartDetailsErrorFields),
          hasError: Boolean(cartDetailsErrorFields?.length),
        },
        { messageId: 'areDeliveryDetailsValid', hasError: areDeliveryDetailsWithError },
        { messageId: 'invalidAddressLine2', hasError: invalidAddressLine2 },
        {
          messageId: 'noDirectPayAndNoPrepaidMethodError',
          hasError: noDirectPayAndNoPrepaidMethodError,
        },
        { messageId: 'invalidReloadError', hasError: invalidReloadError },
        {
          messageId: 'checkoutPaymentMethodNeedRevaultError',
          hasError: checkoutPaymentMethodNeedRevaultError,
        },
        { messageId: 'storeIsClosingError', hasError: storeIsClosing },
      ];
      const paymentErrorMessageIds = paymentErrors.flatMap(item =>
        item.hasError ? [{ ...item }] : []
      );
      return paymentErrorMessageIds;
    },
    [
      cartCateringPriceTooLow,
      cartPriceLimitExceeded,
      cartPriceTooLow,
      getPaymentErrorMessageWithFields,
      omitDeliveryLimitErrors,
      pricedAndAvailable,
    ]
  );

  return {
    getErrorMessages,
  };
};
